import './pos-controls.scss';

import type { HasFiscalPrinter } from '@pos/app/scripts/services/has-fiscal-printer';
import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { FocusStream } from '@pos/app/scripts/services/focus-streams';
import { PosUiState } from '@pos/app/scripts/services/pos-ui-state';
import { SalesCalculations } from '@shared/app/scripts/services/sales-calculations';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { Utility } from '@core/app/scripts/services/utility';

/*
 * pos-controls component.
 */
angular
  .module('posApp')
  .directive(
    'posControls',
    (
      displayMessage: DisplayMessage,
      focusStreams: FocusStream,
      hasFiscalPrinter: HasFiscalPrinter,
      isFiscalReturn,
      navigateTo: NavigateTo,
      pos,
      posUiState: PosUiState,
      salesCalculations: SalesCalculations,
      settingsManager: SettingsManager,
      translate: ng.translate.ITranslateService,
      utility: Utility,
    ) => ({
      restrict: 'E',

      scope: true,

      template: require('./pos-controls.html').default,

      link: function ($scope, element, attrs) {
        const $translate = translate;
        const focusDialogInput = () => {
          window.setTimeout(() => {
            const element = document.getElementById(
              'dialogInput',
            ) as HTMLInputElement | null;
            if (element) {
              element.focus();
              element.select();
            }
          });
        };

        const focusSearchInput = () => {
          window.setTimeout(() => {
            const search = document.getElementById(
              'search',
            ) as HTMLInputElement | null;
            if (search && !search.disabled) {
              search.focus();
              search.select();
            }
          });
        };

        $scope.hasFiscalPrinter = hasFiscalPrinter;
        $scope.displayDiscountReason =
          settingsManager.settings.displayAndPrintDiscountReason;

        // TODO add unit tests
        $scope.isFiscalReturn = isFiscalReturn;

        // TODO add unit tests
        $scope.acls = pos.config.acls;

        // TODO add unit test
        $scope.deleteLine = () => {
          pos.transaction.receipt.splice(posUiState.selectedLine, 1);

          pos.updateTransaction();
          focusSearchInput();
        };

        $scope.openPrice = () => {
          posUiState.dialogCallback = 'changePrice';

          posUiState.dialog.show = true;
          posUiState.dialog.showReasons = true;

          focusDialogInput();

          //set initial value of input field
          const currentReceiptRow =
            pos.transaction.receipt[posUiState.selectedLine];

          posUiState.selected = [currentReceiptRow];

          if (currentReceiptRow) {
            if (currentReceiptRow.category == 'barcode') {
              posUiState.dialog.inputValue = String(
                utility.round2(
                  currentReceiptRow.price * Math.abs(currentReceiptRow.qty),
                ),
              );
              //enable multiselect, disable other buttons
              posUiState.multiLine = true;
              const dot = posUiState.dialog.inputValue.indexOf('.');
              if (dot == -1) {
                posUiState.dialog.inputValue += '.00';
              } else if (dot == posUiState.dialog.inputValue.length - 2) {
                posUiState.dialog.inputValue += '0';
              }
            }
          }
        };

        $scope.openQty = () => {
          posUiState.dialog.show = true;
          posUiState.dialog.showReasons = false;
          posUiState.multiLine = false;
          posUiState.dialog.inputValue =
            pos.transaction.receipt[posUiState.selectedLine].qty;

          focusDialogInput();

          //bind callback to ok button and ng-change.
          posUiState.dialogCallback = 'changeQty';
        };

        $scope.openDiscountPercentage = () => {
          posUiState.dialog.show = true;
          posUiState.dialog.showReasons = true;
          posUiState.multiLine = false;

          focusDialogInput();

          //bind callback to ok button and ng-change.
          posUiState.dialogCallback = 'changeDiscountPercentage';

          posUiState.multiLine = false;
        };

        $scope.openDiscount = () => {
          posUiState.dialog.show = true;
          posUiState.dialog.showReasons = true;

          focusDialogInput();

          //bind callback to ok button and ng-change.
          posUiState.dialogCallback = 'changeDiscount';

          posUiState.multiLine = false;
        };

        $scope.resetArticleList = () => {
          if (pos.transaction && pos.transaction.receipt) {
            if (pos.transaction.receipt.length || pos.transaction.customer) {
              displayMessage.message({
                message: $translate.instant('AreYouSure'),
                type: 'prompt',
                callback: (result) => {
                  if (result == 'yes') {
                    pos.resetTransaction('drop');
                    navigateTo('pos.route#main');
                  }
                },
              });
            }
          } else {
            pos.resetTransaction('drop');
            navigateTo('pos.route#main');
          }
        };

        $scope.applyX4Y = (x, y) => {
          // get the lowest price
          let lowestPrice = Number.MAX_VALUE;
          let subTotal = 0;
          const receipt = pos.transaction.receipt;
          let qty = 0;
          let i = receipt.length - 1;
          const discountableArticles = [];
          let articleIndex = false;
          //collect precise number of articles
          while (x > qty && i >= 0) {
            if (receipt[i].qty > 0 && receipt[i].articleCode) {
              if (receipt[i].qty + qty > x) {
                //Split one line into multiple.
                i = salesCalculations.splitReceiptLine(receipt, i);
              }
              qty += receipt[i].qty;
              if (!articleIndex) {
                articleIndex = i;
              }
              if (!lowestPrice || lowestPrice > receipt[i].nettPrice) {
                lowestPrice = receipt[i].nettPrice;
              }
              subTotal += receipt[i].nettPrice * receipt[i].qty;
              discountableArticles.push(receipt[i]);
            }
            i--;
          }
          if (x > qty) {
            displayMessage.message($translate.instant('Not enough articles'));
            focusSearchInput();
            return;
          }
          //calc = amount of % with which to lower each price
          const calc = (lowestPrice * (x - y)) / subTotal;

          //diff represents cents that need to be added to prevent difference caused by rounding.
          //discount of individual articles added together (renamed from totalDiscount to prevent confusion with transaction.totalDiscount)
          let totalLineDiscount = 0;
          let line;
          for (let j = 0; j < discountableArticles.length; j++) {
            line = discountableArticles[j];
            line.price = line.nettPrice - utility.round2(line.nettPrice * calc);
            line.changeDisc = true;
            totalLineDiscount += line.qty * (line.nettPrice - line.price);
          }
          const diff = utility.round2(
            totalLineDiscount - lowestPrice * (x - y),
          );

          const index = receipt.length - 1;
          if (receipt[articleIndex].qty > 1 && diff !== 0) {
            articleIndex = salesCalculations.splitReceiptLine(
              receipt,
              articleIndex,
            );
          }
          receipt[articleIndex].price += diff;

          pos.updateTransaction();
          focusSearchInput();
        };

        $scope.returnArticle = () => {
          if (settingsManager.settings.useNewReturnButtonWorkflow) {
            if (
              pos.transaction.receipt[posUiState.selectedLine].category ===
              'barcode'
            ) {
              if (pos.transaction.receipt[posUiState.selectedLine].qty > 0) {
                pos.transaction.receipt[posUiState.selectedLine].qty =
                  -pos.transaction.receipt[posUiState.selectedLine].qty;
              }
              if (pos.transaction.receipt[posUiState.selectedLine].qty === 0) {
                pos.transaction.receipt[posUiState.selectedLine].qty = -1;
              }

              pos.updateTransaction();
            }
            focusSearchInput();
          } else {
            pos.returnArticleMode = !pos.returnArticleMode;
            focusStreams.searchSubject.next(undefined);
            focusSearchInput();
          }
        };
      },
    }),
  );
